import { queryOptions, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import TemplateService from './template.service';
import { AxiosResponse } from 'axios';
import useTicketPrint from './templateTicketPrint.options';
import useTemplateMutation from './notification.mutations';
import { queryClient } from '@/lib/queryClient';

interface IUseTemplate {
  mergeTemplatePermission: UseQueryResult<any>;
  downloadOrPrintTemplate: any;
  isDownloadingOrPrintingTicketTemplate: any;
  setIsDownloadingOrPrintingTicketTemplate: any;
}

const useTemplate = (templateId?: number, customerId?: number): IUseTemplate => {
  const { downloadOrPrintTemplate, isDownloadingOrPrintingTicketTemplate, setIsDownloadingOrPrintingTicketTemplate } = useTicketPrint();
  const templateService = TemplateService();

  const mergeTemplatePermission = useQuery<any>({
    queryKey: ['merge-template-permission'],
    queryFn: async () => {
      return new Promise(async (resolve) => {
        await templateService
          .getMergeTemplatePermissions({
            onSuccess: (res: AxiosResponse) => {
              resolve(res.data);
            },
          })
          .then((res) => {
            resolve(res?.data);
          });
      });
    },
    ...queryClient.defaultQueryOptions,
  });

  return {
    mergeTemplatePermission,
    downloadOrPrintTemplate,
    isDownloadingOrPrintingTicketTemplate,
    setIsDownloadingOrPrintingTicketTemplate,
  };
};

export const createMergeTemplatePermissionsOptions = () => {
  const templateService = TemplateService()

  return queryOptions({
    queryKey: ['merge-template-permission'],
    queryFn: async () => {
      const response = await templateService.getMergeTemplatePermissions({})
      return response.data || null
    },
    ...queryClient.defaultQueryOptions,
    retry: 1,
  })
}

export const createCustomFieldsByTemplateIdOptions = (templateId: number) => queryOptions<any>({
  queryKey: ['custom-fields-by-template-id', templateId],
  queryFn: async () => {
    const templateService = TemplateService()
    if (!templateId) return new Promise((resolve) => resolve(null));
    return new Promise(async (resolve) => {
      await templateService
        .getCustomFieldsByTemplateId({
          templateId
        })
        .then((res) => {
          resolve(res?.data);
        });
    });
  },
  refetchInterval: 1000 * 60 * 60, // 1 hour
  enabled: !!templateId,
  refetchOnWindowFocus: false,
});

export const createTemplatesByCustomerOptions = (customerId: number) => queryOptions<any>({
  queryKey: ['templates-by-customer', customerId],
  queryFn: async () => {
    const templateService = TemplateService()
    if (!customerId) return new Promise((resolve) => resolve(null));
    return new Promise(async (resolve) => {
      await templateService
        .getTemplatesByCustomer({
          customerId
        })
        .then((res) => {
          resolve(res?.data);
        });
    });
  },
  ...queryClient.defaultQueryOptions,
  enabled: !!customerId,
});

export default useTemplate;
